import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { 
  Calendar, Clock, CheckCircle2, Circle, ArrowRight, 
  FileText, RefreshCw, X, ChevronDown, ChevronUp,
  Loader2, ListTodo, Sparkles
} from 'lucide-react';

const API = window.__WR_API_BASE__;

/**
 * Weekend Catchup View - Batch process incomplete notes
 * Shows all notes from the week, sorted oldest first
 * Allows marking as done and loading into editor
 */
const WeekendCatchup = ({ 
  show, 
  onClose, 
  userId, 
  onLoadNote,
  currentNoteId 
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doneEntries, setDoneEntries] = useState(() => {
    try {
      const saved = localStorage.getItem('writeright_done_entries');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [dateFilter, setDateFilter] = useState('week'); // 'today', 'week', 'all'

  // Load history entries
  useEffect(() => {
    if (show && userId) {
      loadEntries();
    }
  }, [show, userId]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/history?limit=500`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Backend returns a plain array
        setEntries(Array.isArray(data) ? data : (data.history || []));
      }
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Filter entries by date
  const filteredEntries = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay()); // Sunday

    return entries.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      
      if (dateFilter === 'today') {
        return entryDate >= today;
      } else if (dateFilter === 'week') {
        return entryDate >= startOfWeek;
      }
      return true; // 'all'
    }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // Oldest first
  }, [entries, dateFilter]);

  // Calculate progress
  const totalEntries = filteredEntries.length;
  const completedEntries = filteredEntries.filter(e => doneEntries[e.id]).length;
  const progressPercent = totalEntries > 0 ? (completedEntries / totalEntries) * 100 : 0;

  // Toggle done status (with undo)
  const toggleDone = (entryId) => {
    setDoneEntries(prev => {
      const wasDone = prev[entryId];
      const updated = { ...prev, [entryId]: !wasDone };
      localStorage.setItem('writeright_done_entries', JSON.stringify(updated));
      if (!wasDone) {
        toast.success('Marked done', {
          action: {
            label: 'Undo',
            onClick: () => {
              setDoneEntries(p => {
                const reverted = { ...p };
                delete reverted[entryId];
                localStorage.setItem('writeright_done_entries', JSON.stringify(reverted));
                return reverted;
              });
            },
          },
          duration: 4000,
        });
      }
      return updated;
    });
  };

  // Mark all as done
  const markAllDone = () => {
    const updated = { ...doneEntries };
    filteredEntries.forEach(e => { updated[e.id] = true; });
    setDoneEntries(updated);
    localStorage.setItem('writeright_done_entries', JSON.stringify(updated));
    toast.success('All marked as done!');
  };

  // Clear all done marks (with undo)
  const clearAllDone = () => {
    const previousState = { ...doneEntries };
    const updated = { ...doneEntries };
    filteredEntries.forEach(e => { delete updated[e.id]; });
    setDoneEntries(updated);
    localStorage.setItem('writeright_done_entries', JSON.stringify(updated));
    toast.success('All marks cleared', {
      action: {
        label: 'Undo',
        onClick: () => {
          setDoneEntries(previousState);
          localStorage.setItem('writeright_done_entries', JSON.stringify(previousState));
          toast.success('Marks restored');
        },
      },
      duration: 5000,
    });
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });

    if (date >= today) {
      return `Today ${timeStr}`;
    } else if (date >= yesterday) {
      return `Yesterday ${timeStr}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric' 
      }) + ` ${timeStr}`;
    }
  };

  // Get day label for grouping
  const getDayLabel = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date >= today) return 'Today';
    if (date >= yesterday) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Group entries by day
  const groupedEntries = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(entry => {
      const dayLabel = getDayLabel(entry.timestamp);
      if (!groups[dayLabel]) {
        groups[dayLabel] = [];
      }
      groups[dayLabel].push(entry);
    });
    return groups;
  }, [filteredEntries]);

  // Get patient summary
  const getPatientSummary = (entry) => {
    const parts = [];
    if (entry.patient_age) parts.push(`${entry.patient_age}yo`);
    if (entry.patient_gender) parts.push(entry.patient_gender);
    if (entry.patient_status) {
      const statusMap = {
        'active_duty': 'AD',
        'veteran': 'Vet',
        'dependent': 'Dep',
        'reserve': 'Res',
        'national_guard': 'NG',
        'civilian': 'Civ'
      };
      parts.push(statusMap[entry.patient_status] || entry.patient_status);
    }
    return parts.join(' ') || 'Patient';
  };

  // Get chief complaint or first line of notes
  const getChiefComplaint = (entry) => {
    if (entry.chief_complaint) return entry.chief_complaint;
    if (entry.original_notes) {
      const firstLine = entry.original_notes.split('\n')[0].substring(0, 60);
      return firstLine + (entry.original_notes.length > 60 ? '...' : '');
    }
    return 'No notes';
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-card border-border">
        <CardHeader className="border-b border-border pb-4 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <ListTodo className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Weekend Catchup</CardTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Work through your notes, oldest first
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">
                {completedEntries} / {totalEntries} done
                {progressPercent === 100 && <Sparkles className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Filters & Actions */}
          <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
            <div className="flex gap-2">
              <Button 
                variant={dateFilter === 'today' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setDateFilter('today')}
              >
                Today
              </Button>
              <Button 
                variant={dateFilter === 'week' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setDateFilter('week')}
              >
                This Week
              </Button>
              <Button 
                variant={dateFilter === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setDateFilter('all')}
              >
                All Time
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadEntries} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={clearAllDone}>
                Uncheck All
              </Button>
              <Button variant="default" size="sm" onClick={markAllDone}>
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Mark All Done
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mb-3 text-green-500" />
              <p className="text-lg font-medium">All caught up!</p>
              <p className="text-sm">No notes found for this period</p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(90vh-280px)]">
              <div className="p-4 space-y-6">
                {Object.entries(groupedEntries).map(([dayLabel, dayEntries]) => (
                  <div key={dayLabel}>
                    {/* Day Header */}
                    <div className="flex items-center gap-2 mb-3 sticky top-0 bg-card py-2 z-10">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{dayLabel}</span>
                      <Badge variant="secondary" className="text-xs">
                        {dayEntries.filter(e => doneEntries[e.id]).length}/{dayEntries.length}
                      </Badge>
                    </div>

                    {/* Entries for this day */}
                    <div className="space-y-2">
                      {dayEntries.map((entry, index) => {
                        const isDone = doneEntries[entry.id];
                        const isExpanded = expandedEntry === entry.id;
                        const isCurrent = currentNoteId === entry.id;

                        return (
                          <div
                            key={entry.id}
                            className={`border rounded-lg transition-all ${
                              isDone 
                                ? 'border-green-500/30 bg-green-500/5 opacity-60' 
                                : isCurrent
                                  ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                                  : 'border-border bg-card hover:border-primary/50'
                            }`}
                          >
                            <div className="p-3 flex items-center gap-3">
                              {/* Checkbox */}
                              <Checkbox
                                checked={isDone}
                                onCheckedChange={() => toggleDone(entry.id)}
                                className={`h-5 w-5 shrink-0 ${isDone ? 'data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500' : ''}`}
                              />

                              {/* Entry Number */}
                              <span className={`text-xs font-mono w-6 shrink-0 ${isDone ? 'text-green-500' : 'text-muted-foreground'}`}>
                                #{index + 1}
                              </span>

                              {/* Patient Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`font-medium text-sm ${isDone ? 'line-through' : ''}`}>
                                    {getPatientSummary(entry)}
                                  </span>
                                  {entry.letter_type && (
                                    <Badge variant="outline" className="text-xs">
                                      {entry.letter_type}
                                    </Badge>
                                  )}
                                  {entry.imaging_orders?.length > 0 && (
                                    <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500">
                                      {entry.imaging_orders.length} Img
                                    </Badge>
                                  )}
                                  {entry.referrals?.length > 0 && (
                                    <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-500">
                                      {entry.referrals.length} Ref
                                    </Badge>
                                  )}
                                  {entry.clinical_note && (
                                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-500">Note</Badge>
                                  )}
                                  {entry.icd10_suggestions?.length > 0 && (
                                    <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-500">
                                      {entry.icd10_suggestions.length} ICD-10
                                    </Badge>
                                  )}
                                </div>
                                <p className={`text-xs text-muted-foreground truncate mt-0.5 ${isDone ? 'line-through' : ''}`}>
                                  {getChiefComplaint(entry)}
                                </p>
                                {/* Notes preview */}
                                {entry.original_notes && (
                                  <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5 italic">
                                    {entry.original_notes.substring(0, 100)}{entry.original_notes.length > 100 ? '...' : ''}
                                  </p>
                                )}
                              </div>

                              {/* Time */}
                              <span className="text-xs text-muted-foreground shrink-0">
                                {formatTime(entry.timestamp).split(' ').slice(-2).join(' ')}
                              </span>

                              {/* Actions */}
                              <div className="flex items-center gap-1 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </Button>
                                <Button
                                  variant={isCurrent ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    onLoadNote(entry);
                                    toast.success('Note loaded');
                                  }}
                                  className="h-8"
                                >
                                  {isCurrent ? 'Current' : 'Load'}
                                  <ArrowRight className="w-3 h-3 ml-1" />
                                </Button>
                              </div>
                            </div>

                            {/* Expanded Content */}
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-0 border-t border-border mt-2">
                                <div className="grid grid-cols-2 gap-4 mt-3">
                                  {entry.clinical_note && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground mb-1">Clinical Note</p>
                                      <p className="text-xs bg-secondary p-2 rounded max-h-32 overflow-y-auto whitespace-pre-wrap">
                                        {entry.clinical_note.substring(0, 500)}...
                                      </p>
                                    </div>
                                  )}
                                  {entry.icd10_suggestions?.length > 0 && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground mb-1">ICD-10 Codes</p>
                                      <div className="flex flex-wrap gap-1">
                                        {entry.icd10_suggestions.map((code, i) => (
                                          <Badge key={i} variant="secondary" className="text-xs">
                                            {code.code}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WeekendCatchup;
